import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { PageStatus } from 'libs/design-system/src/components/PageStatus';
import { CircleExclamation } from '@unified-checkout/design-system/icons/index';

import type { ITransferDetails } from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer';
import { getSingleTransferDetails } from '@app/dashboard/services/inner_layout/balance_and_transfers/single-transfer';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { BALANCE_TRANSFERS_EVENTS_VALUES } from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { useLangStore } from '@app/dashboard/store/lang';
import { useCancelQuery } from '@app/dashboard/hooks/useCancelQuery';

import { useSingleTransferViewTracking } from '../../../hooks/useSingleTransferViewTracking';
import Header from './Header';
import Progress from './Progress';
import Summary from './Summary';
import Breakdown from './Breakdown';

import styles from './single-transfer.module.scss';

const SingleTransfer = () => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const { id } = useParams<{ id: string }>();

  const { data, isError, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_TRANSFER_DETAILS, id, lang],
    queryFn: ({ signal }) => getSingleTransferDetails(id as string, signal),
    enabled: !!id,
    select: (response) => response?.data?.data as ITransferDetails,
  });

  const {
    expected_arrival = null,
    transfer_summary,
    actions = {
      ach_document_url: '',
      csv_url: '',
      pdf_url: '',
    },
    transfer_breakdown,
    header,
    transfer_progress,
  } = data || {};

  useCancelQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_TRANSFER_DETAILS, id, lang],
    deps: [id, lang],
  });

  useSingleTransferViewTracking({
    id,
    isError,
    isFetching,
    header,
    transfer_progress,
    transfer_summary,
    entryPoint: BALANCE_TRANSFERS_EVENTS_VALUES.DETAILS,
  });

  return isError ? (
    <PageStatus
      status="error"
      title={t('innerLayout.errors.smthWentWrong')}
      description={t('innerLayout.errors.dataRetrievalErr')}
      icon={CircleExclamation}
    />
  ) : (
    <div className={styles['single-transfer']}>
      <Header
        isLoading={isFetching}
        actions={actions}
        transferId={id as string}
        header={header}
        transactionCount={transfer_breakdown?.transaction_count || 0}
      />
      <div className={styles['single-transfer__details']}>
        <Progress
          isLoading={isFetching}
          header={header}
          transferProgress={transfer_progress}
          transferId={transfer_summary?.transfer_id}
          settlementType={transfer_summary?.settlement_type_key}
        />
        <Summary
          header={header}
          isLoading={isFetching}
          transferSummary={transfer_summary}
          expectedArrival={expected_arrival}
          transactionCount={transfer_breakdown?.transaction_count || 0}
        />
      </div>
      <Breakdown
        breakdown={transfer_breakdown}
        isLoading={isFetching}
        header={header}
        transferId={transfer_summary?.transfer_id}
      />
    </div>
  );
};

export default SingleTransfer;
